import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Mail, MessageCircle, Clock, Phone } from "lucide-react";
import { SITE, buildWaLink } from "../data/site";
import { useLang } from "../data/i18n";
import { trackLead } from "./Analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
    const { t } = useLang();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        experience: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error(t.contact.errorRequired);
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/leads`, { ...form, source: "contact_form" });
            trackLead({ experience: form.experience });
            setSubmitted(true);
            setForm({
                name: "",
                email: "",
                phone: "",
                experience: "",
                message: "",
            });
        } catch (err) {
            toast.error(t.contact.errorGeneric);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            className="relative bg-cream py-24 md:py-32 paper-grain"
            data-testid="contact-section"
        >
            <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Left */}
                    <div className="lg:col-span-5">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-coral font-bold mb-5 flex items-center gap-3">
                            <span className="inline-block w-8 h-px bg-coral" />
                            {t.contact.kicker}
                        </p>
                        <h2 className="font-serif font-normal text-ocean text-5xl md:text-7xl leading-[0.95] tracking-tight">
                            {t.contact.titleA}
                            <br />
                            <em className="coral-italic">{t.contact.titleB}</em>.
                        </h2>

                        <div className="mt-12 space-y-8">
                            <a
                                href={buildWaLink(t.contact.waMessage)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-start gap-5 group"
                                data-testid="contact-whatsapp-link"
                            >
                                <div className="w-12 h-12 flex items-center justify-center bg-coral text-cream flex-shrink-0 group-hover:scale-110 transition-transform">
                                    <MessageCircle size={20} strokeWidth={1.6} />
                                </div>
                                <div>
                                    <p className="text-ink/60 text-[10px] uppercase tracking-[0.3em] font-bold mb-1">
                                        {t.contact.whatsappLabel}
                                    </p>
                                    <p className="text-ocean text-lg font-semibold group-hover:text-coral transition-colors">
                                        {SITE.whatsapp.number}
                                    </p>
                                </div>
                            </a>
                            <a
                                href={`tel:${SITE.phone.replace(/\s/g, "")}`}
                                className="flex items-start gap-5 group"
                                data-testid="contact-phone-link"
                            >
                                <div className="w-12 h-12 flex items-center justify-center bg-lagoon-700 text-cream flex-shrink-0 group-hover:scale-110 transition-transform">
                                    <Phone size={20} strokeWidth={1.6} />
                                </div>
                                <div>
                                    <p className="text-ink/60 text-[10px] uppercase tracking-[0.3em] font-bold mb-1">
                                        {t.contact.phoneLabel}
                                    </p>
                                    <p className="text-ocean text-lg font-semibold group-hover:text-coral transition-colors">
                                        {SITE.phone}
                                    </p>
                                </div>
                            </a>
                            <a
                                href={`mailto:${SITE.email}`}
                                className="flex items-start gap-5 group"
                                data-testid="contact-email-link"
                            >
                                <div className="w-12 h-12 flex items-center justify-center bg-ocean text-cream flex-shrink-0 group-hover:scale-110 transition-transform">
                                    <Mail size={20} strokeWidth={1.6} />
                                </div>
                                <div>
                                    <p className="text-ink/60 text-[10px] uppercase tracking-[0.3em] font-bold mb-1">
                                        {t.contact.emailLabel}
                                    </p>
                                    <p className="text-ocean text-lg font-semibold group-hover:text-coral transition-colors">
                                        {SITE.email}
                                    </p>
                                </div>
                            </a>
                            <div className="flex items-start gap-5">
                                <div className="w-12 h-12 flex items-center justify-center bg-lagoon text-ocean flex-shrink-0">
                                    <Clock size={20} strokeWidth={1.6} />
                                </div>
                                <div>
                                    <p className="text-ink/60 text-[10px] uppercase tracking-[0.3em] font-bold mb-1">
                                        {t.contact.delayLabel}
                                    </p>
                                    <p className="text-ocean text-lg font-semibold">
                                        {t.contact.delayValue}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form or success card */}
                    {submitted ? (
                        <motion.div
                            initial={{ opacity: 0, y: 20, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.6 }}
                            className="lg:col-span-7 bg-white border-2 border-coral/50 p-10 md:p-14 shadow-[0_10px_40px_rgba(235,103,65,0.15)] rounded-2xl text-center flex flex-col items-center justify-center"
                            data-testid="contact-success"
                            role="status"
                            aria-live="polite"
                        >
                            <div className="w-20 h-20 rounded-full bg-coral/10 flex items-center justify-center mb-6">
                                <svg
                                    width="36"
                                    height="36"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#EB6741"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>
                            <p className="text-[11px] uppercase tracking-[0.3em] text-coral font-bold mb-3">
                                {t.contact.successKicker}
                            </p>
                            <h3 className="font-serif text-3xl md:text-4xl text-ocean mb-4 leading-tight">
                                {t.contact.successTitleA}
                                <br />
                                <em className="coral-italic">{t.contact.successTitleB}</em>
                            </h3>
                            <p className="text-ink/75 text-base max-w-md leading-relaxed mb-8">
                                {t.contact.successText}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <a
                                    href={buildWaLink(t.contact.successWaMessage)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-coral justify-center"
                                    data-testid="success-whatsapp"
                                >
                                    {t.contact.successWa}
                                </a>
                                <button
                                    onClick={() => setSubmitted(false)}
                                    className="px-6 py-3 border-2 border-ocean/30 text-ocean uppercase tracking-[0.18em] text-xs font-semibold hover:bg-ocean hover:text-cream transition-colors rounded-lg"
                                    data-testid="success-new"
                                >
                                    {t.contact.successNew}
                                </button>
                            </div>
                        </motion.div>
                    ) : (
                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-7 bg-white border-2 border-coral/50 p-8 md:p-12 space-y-6 shadow-[0_20px_60px_rgba(235,103,65,0.18)] rounded-2xl"
                        data-testid="contact-form"
                    >
                        <div className="flex justify-center">
                            <span
                                className="inline-flex items-center gap-2 bg-coral text-cream text-[10px] uppercase tracking-[0.25em] font-bold px-4 py-2 rounded-full shadow-md"
                                data-testid="contact-form-badge"
                            >
                                {t.contact.badge}
                            </span>
                        </div>
                        <FormField label={t.contact.nameLabel}>
                            <input
                                type="text"
                                value={form.name}
                                onChange={update("name")}
                                placeholder={t.contact.namePh}
                                autoComplete="name"
                                required
                                className="w-full bg-transparent border-b-2 border-ocean/20 focus:border-coral py-3 text-ink placeholder:text-ink/40 outline-none transition-colors"
                                data-testid="contact-input-name"
                            />
                        </FormField>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField label={t.contact.emailLabel}>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    placeholder={t.contact.emailPh}
                                    autoComplete="email"
                                    inputMode="email"
                                    required
                                    className="w-full bg-transparent border-b-2 border-ocean/20 focus:border-coral py-3 text-ink placeholder:text-ink/40 outline-none transition-colors"
                                    data-testid="contact-input-email"
                                />
                            </FormField>
                            <FormField label={t.contact.phoneLabel}>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    placeholder={t.contact.phonePh}
                                    autoComplete="tel"
                                    inputMode="tel"
                                    className="w-full bg-transparent border-b-2 border-ocean/20 focus:border-coral py-3 text-ink placeholder:text-ink/40 outline-none transition-colors"
                                    data-testid="contact-input-phone"
                                />
                            </FormField>
                        </div>

                        <FormField label={t.contact.experienceLabel}>
                            <select
                                value={form.experience}
                                onChange={update("experience")}
                                className="w-full bg-transparent border-b-2 border-ocean/20 focus:border-coral py-3 text-ink outline-none transition-colors appearance-none cursor-pointer"
                                data-testid="contact-select-experience"
                            >
                                <option value="">{t.contact.choose}</option>
                                {t.contact.expOptions.map((o) => (
                                    <option key={o} value={o}>
                                        {o}
                                    </option>
                                ))}
                            </select>
                        </FormField>

                        <FormField label={t.contact.messageLabel}>
                            <textarea
                                value={form.message}
                                onChange={update("message")}
                                placeholder={t.contact.messagePh}
                                rows={4}
                                className="w-full bg-cream/60 border-2 border-ocean/15 focus:border-coral p-4 text-ink placeholder:text-ink/40 outline-none transition-colors resize-none"
                                data-testid="contact-input-message"
                            />
                        </FormField>

                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-4">
                            <p className="text-ink/70 text-xs">
                                {t.contact.replyNoteA}
                                <span className="text-coral font-bold">{t.contact.replyNoteB}</span>.
                            </p>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-coral disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="contact-submit"
                            >
                                {loading ? t.contact.sending : t.contact.submit}
                                <span>→</span>
                            </button>
                        </div>
                    </motion.form>
                    )}
                </div>
            </div>
        </section>
    );
}

function FormField({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.3em] font-bold text-ocean mb-2">
                {label}
            </span>
            {children}
        </label>
    );
}
